import type {
  Item,
  ItemKind,
  PlanResponse,
  DecideResponse,
  DailyResponse,
  User,
} from './types';
import type { Locale } from './i18n';

const BASE = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/+$/, '');

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    let message = text;
    try {
      const parsed = JSON.parse(text);
      message = parsed.message ?? parsed.error ?? text;
    } catch {
      /* noop */
    }
    throw new Error(`${res.status}: ${message}`);
  }
  return (await res.json()) as T;
}

export const API_BASE = BASE;

export const api = {
  me: () => request<{ user: User }>('/auth/me'),

  logout: () => request<{ ok: boolean }>('/auth/logout', { method: 'POST' }),

  yandexSignInUrl: () => `${BASE}/auth/yandex/start`,

  capture: (content: string, kind: ItemKind, tags: string[]) =>
    request<{ item: Item }>('/capture', {
      method: 'POST',
      body: JSON.stringify({ content, kind, tags }),
    }),

  items: (opts?: { kind?: ItemKind; tag?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (opts?.kind) qs.set('kind', opts.kind);
    if (opts?.tag) qs.set('tag', opts.tag);
    if (opts?.limit) qs.set('limit', String(opts.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<{ items: Item[]; total: number }>(`/items${suffix}`);
  },

  plan: (horizon: 'today' | 'week' = 'today', focus?: string, locale?: Locale) =>
    request<PlanResponse>('/plan', {
      method: 'POST',
      body: JSON.stringify({ horizon, focus, locale }),
    }),

  decide: (decision: string, context?: string, options?: string[], locale?: Locale) =>
    request<DecideResponse>('/decide', {
      method: 'POST',
      body: JSON.stringify({ decision, context, options, locale }),
    }),

  daily: (focus?: string, date?: string, locale?: Locale) =>
    request<DailyResponse>('/daily', {
      method: 'POST',
      body: JSON.stringify({ focus, date, locale }),
    }),
};
